import { Component, Input, ChangeDetectorRef, OnDestroy, inject, input, output, TemplateRef } from '@angular/core';
import { NgStyle } from '@angular/common';
import { columnsByPin, columnGroupWidths, columnsByPinArr } from '../../utils/column';
import { SortType } from '../../types/sort.type';
import { SelectionType } from '../../types/selection.type';
import { TableColumn } from '../../types/table-column.type';
import { SortPropDir } from '../../types/sort-prop-dir.type';
import { SortDirection } from '../../types/sort-direction.type';
import { translateXY } from '../../utils/translate';
import { DraggableDirective } from '../../directives/draggable.directive';
import { LongPressDirective } from '../../directives/long-press.directive';
import { ResizeableDirective } from '../../directives/resizeable.directive';
import { OrderableDirective } from '../../directives/orderable.directive';
import { DataTableHeaderCellComponent } from './header-cell.component';

interface ColumnGroupWidths {
  left: number;
  center: number;
  right: number;
  total: number;
  [key: string]: number;
}

interface ColumnPinGroup {
  type: string;
  columns: Array<TableColumn>;
}

interface ReorderEvent {
  prevIndex: number;
  newIndex: number;
  model: unknown;
}

interface TargetChangedEvent {
  prevIndex?: number;
  newIndex?: number;
  initialIndex?: number;
}

interface SortEvent {
  column: TableColumn;
  prevValue: unknown;
  newValue: unknown;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'datatable-header',
  template: `
      <div role="row"
           orderable
           (reorder)="onColumnReordered($event)"
           (targetChanged)="onTargetChanged($event)"
           [style.width.px]="_columnGroupWidths.total"
           class="datatable-header-inner">
        @for (colGroup of _columnsByPin; track trackByGroups($index, colGroup)) {
          <div [class]="'datatable-row-' + colGroup.type"
               [ngStyle]="_styleByGroup[colGroup.type]">
            @for (column of colGroup.columns; track columnTrackingFn($index, column)) {
              <datatable-header-cell role="columnheader"
                resizeable
                [resizeEnabled]="column.resizeable"
                (resize)="onColumnResized($event, column)"
                long-press
                [pressModel]="column"
                [pressEnabled]="reorderable() && column.draggable"
                (longPressStart)="onLongPressStart($event)"
                (longPressEnd)="onLongPressEnd($event)"
                draggable
                [dragX]="reorderable() && column.draggable && column.dragging"
                [dragY]="false"
                [dragModel]="column"
                [dragEventTarget]="dragEventTarget"
                [headerHeight]="_rawHeaderHeight"
                [isTarget]="column.isTarget"
                [targetMarkerTemplate]="targetMarkerTemplate()"
                [targetMarkerContext]="column.targetMarkerContext"
                [column]="column"
                [sortType]="sortType()"
                [sorts]="sorts()"
                [selectionType]="selectionType()"
                [sortAscendingIcon]="sortAscendingIcon()"
                [sortDescendingIcon]="sortDescendingIcon()"
                [sortUnsetIcon]="sortUnsetIcon()"
                [allRowsSelected]="allRowsSelected()"
                (sort)="onSort($event)"
                (select)="select.emit($event)"
                (columnContextmenu)="columnContextmenu.emit($event)" />
            }
          </div>
        }
      </div>
    `,
  host: {
    class: 'datatable-header',
    '[style.width]': 'headerWidth',
    '[style.height]': 'headerHeight'
  },
  imports: [OrderableDirective, NgStyle, DataTableHeaderCellComponent, ResizeableDirective, LongPressDirective, DraggableDirective]
})
export class DataTableHeaderComponent implements OnDestroy {
  private readonly cd = inject(ChangeDetectorRef);

  readonly sortAscendingIcon = input<string>(undefined);
  readonly sortDescendingIcon = input<string>(undefined);
  readonly sortUnsetIcon = input<string>(undefined);
  readonly scrollbarH = input<boolean>(undefined);
  readonly dealsWithGroup = input<boolean>(undefined);
  readonly targetMarkerTemplate = input<TemplateRef<unknown>>(undefined);

  targetMarkerContext: unknown;

  @Input() set innerWidth(val: number) {
    this._innerWidth = val;
    setTimeout(() => {
      if (this._columns) {
        const colByPin = columnsByPin(this._columns);
        this._columnGroupWidths = columnGroupWidths(colByPin, this._columns);
        this.setStylesByGroup();
      }
    });
  }

  get innerWidth(): number {
    return this._innerWidth;
  }

  readonly sorts = input<Array<SortPropDir>>(undefined);
  readonly sortType = input<SortType>(undefined);
  readonly allRowsSelected = input<boolean>(undefined);
  readonly selectionType = input<SelectionType>(undefined);
  readonly reorderable = input<boolean>(undefined);

  dragEventTarget: unknown;

  @Input()
  set headerHeight(val: number | string) {
    this._rawHeaderHeight = typeof val === 'number' ? val : undefined;
    if (val !== 'auto') {
      this._headerHeight = `${val}px`;
    } else {
      this._headerHeight = val;
    }
  }

  get headerHeight(): number | string {
    return this._headerHeight;
  }

  @Input() set columns(val: Array<unknown>) {
    this._columns = val;

    const colsByPin = columnsByPin(val);
    this._columnsByPin = columnsByPinArr(val);
    setTimeout(() => {
      this._columnGroupWidths = columnGroupWidths(colsByPin, val);
      this.setStylesByGroup();
    });
  }

  get columns(): Array<unknown> {
    return this._columns;
  }

  @Input()
  set offsetX(val: number) {
    this._offsetX = val;
    this.setStylesByGroup();
  }

  get offsetX(): number {
    return this._offsetX;
  }

  readonly sort = output<{ sorts: Array<SortPropDir>; column: TableColumn; prevValue: unknown; newValue: unknown }>();
  readonly reorder = output<{ column: unknown; prevValue: number; newValue: number }>();
  readonly resize = output<{ column: TableColumn; prevValue: number; newValue: number }>();
  readonly select = output<unknown>();
  readonly columnContextmenu = output<{
    event: MouseEvent;
    column: unknown;
  }>();

  _columnsByPin: Array<ColumnPinGroup> = [];
  _columnGroupWidths: ColumnGroupWidths = { left: 0, center: 0, right: 0, total: 100 };

  _innerWidth: number;
  _offsetX: number;
  _columns: Array<unknown>;
  _headerHeight: string;
  _rawHeaderHeight: number;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  _styleByGroup: Record<string, {}> = {
    left: {},
    center: {},
    right: {}
  };

  private destroyed = false;

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  onLongPressStart({ event, model }: { event: unknown; model: TableColumn }): void {
    model.dragging = true;
    this.dragEventTarget = event;
  }

  onLongPressEnd({ event, model }: { event?: MouseEvent; model: TableColumn }): void {
    this.dragEventTarget = event;

    setTimeout(() => {
      const column = this._columns.find((c: unknown) => (c as TableColumn).$$id === model.$$id) as TableColumn | undefined;
      if (column) {
        column.dragging = false;
      }
    }, 5);
  }

  get headerWidth(): string {
    if (this.scrollbarH()) {
      return this.innerWidth + 'px';
    }

    return '100%';
  }

  trackByGroups(_index: number, colGroup: ColumnPinGroup): string {
    return colGroup.type;
  }

  columnTrackingFn(_index: number, column: TableColumn): string {
    return column.$$id;
  }

  onColumnResized(width: number, column: TableColumn): void {
    if (column.minWidth && width <= column.minWidth) {
      width = column.minWidth;
    } else if (column.maxWidth && width >= column.maxWidth) {
      width = column.maxWidth;
    }

    this.resize.emit({
      column,
      prevValue: column.width,
      newValue: width
    });
  }

  onColumnReordered({ prevIndex, newIndex, model }: ReorderEvent): void {
    const column = this.getColumn(newIndex);
    column.isTarget = false;
    column.targetMarkerContext = undefined;
    this.reorder.emit({
      column: model,
      prevValue: prevIndex,
      newValue: newIndex
    });
  }

  onTargetChanged({ prevIndex, newIndex, initialIndex }: TargetChangedEvent): void {
    if (prevIndex || prevIndex === 0) {
      const oldColumn = this.getColumn(prevIndex);
      oldColumn.isTarget = false;
      oldColumn.targetMarkerContext = undefined;
    }
    if (newIndex || newIndex === 0) {
      const newColumn = this.getColumn(newIndex);
      newColumn.isTarget = true;

      if (initialIndex !== newIndex) {
        newColumn.targetMarkerContext = {
          class: 'targetMarker '.concat(initialIndex > newIndex ? 'dragFromRight' : 'dragFromLeft')
        };
      }
    }
  }

  getColumn(index: number): TableColumn {
    const leftColumnCount = this._columnsByPin[0].columns.length;
    if (index < leftColumnCount) {
      return this._columnsByPin[0].columns[index];
    }

    const centerColumnCount = this._columnsByPin[1].columns.length;
    if (index < leftColumnCount + centerColumnCount) {
      return this._columnsByPin[1].columns[index - leftColumnCount];
    }

    return this._columnsByPin[2].columns[index - leftColumnCount - centerColumnCount];
  }

  onSort({ column, prevValue, newValue }: SortEvent): void {
    if (column.dragging) {
      return;
    }

    const sorts = this.calcNewSorts(column, prevValue, newValue);
    this.sort.emit({
      sorts,
      column,
      prevValue,
      newValue
    });
  }

  calcNewSorts(column: TableColumn, prevValue: unknown, newValue: unknown): Array<SortPropDir> {
    let idx = 0;

    const sortsValue = (this.sorts() ?? []);

    const sorts = sortsValue.map((s: SortPropDir, i: number) => {
      s = { ...s };
      if (s.prop === column.prop) {
        idx = i;
      }
      return s;
    });

    if (newValue === undefined) {
      sorts.splice(idx, 1);
    } else if (prevValue) {
      sorts[idx].dir = newValue as SortDirection;
    } else {
      if (this.sortType() === SortType.single) {
        sorts.splice(0, sortsValue.length);
      }

      sorts.push({ dir: newValue as SortDirection, prop: column.prop });
    }

    return sorts;
  }

  setStylesByGroup(): void {
    this._styleByGroup.left = this.calcStylesByGroup('left');
    this._styleByGroup.center = this.calcStylesByGroup('center');
    this._styleByGroup.right = this.calcStylesByGroup('right');
    if (!this.destroyed) {
      this.cd.detectChanges();
    }
  }

  calcStylesByGroup(group: string): Record<string, string> {
    const widths = this._columnGroupWidths;
    const offsetX = this.offsetX;

    const styles: Record<string, string> = {
      width: `${widths[group]}px`
    };

    if (group === 'center') {
      translateXY(styles, offsetX * -1, 0);
    } else if (group === 'right') {
      const totalDiff = widths.total - this.innerWidth;
      const offset = totalDiff * -1;
      translateXY(styles, offset, 0);
    }

    return styles;
  }
}
